import { ReactNode } from 'react';
import type {
  UserProject,
  UserProjectSubmission,
  CourseReview,
  Note,
  ScheduledCall,
  HearingSubmissionSummary,
} from '@teachur/http-client';
import { getSortedProjectGoals } from '@new/features/projects-review/utils/projectReviewDrawerHelpers';
import { formatSubmittedDate } from '@new/features/projects-review/utils/projectReviewFormatters';
import { formatShortDate } from '@new/common/lib/dateTime';

export interface SubmissionDetailData {
  title: string;
  date?: string;
  instructorName?: string;
  statusBadge?: ReactNode;
  goals: { goal: { id: number; text: string }; rating: number; note: string }[];
  generalNote: string;
  rejectionReason?: string | null;
  cancelledByName?: string;
}

export const toProjectSubmissionDetail = (
  userProject: UserProject,
  submission: UserProjectSubmission,
  statusBadge?: ReactNode
): SubmissionDetailData => {
  const goals = getSortedProjectGoals(userProject.project?.goals);
  return {
    title: userProject.project?.name ?? 'Submission Details',
    date: formatSubmittedDate(submission.createdAt),
    instructorName: submission.reviewedBy
      ? `${submission.reviewedBy.firstName} ${submission.reviewedBy.lastName}`
      : userProject.instructor
        ? `${userProject.instructor.firstName} ${userProject.instructor.lastName}`
        : undefined,
    statusBadge,
    goals: goals.map((goal) => {
      const grade = submission.grades?.find((g) => g.goalId === goal.id);
      return { goal, rating: grade?.grade ?? 0, note: grade?.note ?? '' };
    }),
    generalNote: submission.note ?? '',
  };
};

export const toScheduledCallSubmissionDetail = (
  call: ScheduledCall,
  statusBadge: ReactNode,
  review?: CourseReview,
  notes?: Note[]
): SubmissionDetailData => ({
  title: new Date(call.startTime * 1000).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }),
  date: new Date(call.startTime * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  }),
  instructorName: review?.instructor
    ? `${review.instructor.firstName} ${review.instructor.lastName}`
    : call.instructor
      ? `${call.instructor.firstName} ${call.instructor.lastName}`
      : undefined,
  statusBadge,
  goals: (review?.grades ?? []).map((grade) => ({
    goal: grade.goal ?? { id: grade.goalId!, text: `Goal #${grade.goalId}` },
    rating: grade.grade,
    note: grade.note ?? '',
  })),
  generalNote: (notes ?? []).map((n) => n.text).join('\n\n'),
});

// Maps a new-model HearingSubmissionSummary (from GET /course-hearings) to the
// shared submission-detail shape. Replaces the legacy CourseReview + Note based
// mapper now that hearing reviews live on the hearing-submission model.

// `dateOverride` lets the caller supply the date to show. Pass a formatted
// string to override, or `null` to hide the date entirely (e.g. legacy
// previousReviews whose createdAt is a backfill placeholder). When omitted the
// mapper falls back to the submission's createdAt.
export const toHearingSubmissionDetail = (
  submission: HearingSubmissionSummary,
  statusBadge: ReactNode,
  dateOverride?: string | null
): SubmissionDetailData => ({
  title: 'Course Hearing Review',

  date:
    dateOverride === undefined
      ? formatShortDate(submission.createdAt)
      : (dateOverride ?? undefined),

  instructorName: submission.reviewedBy
    ? `${submission.reviewedBy.firstName} ${submission.reviewedBy.lastName}`
    : undefined,

  statusBadge,

  goals: (submission.grades ?? []).map((grade) => ({
    goal: grade.goal ?? {
      id: grade.goalId,
      text: `Goal #${grade.goalId}`,
    },
    rating: grade.grade,
    note: grade.note ?? '',
  })),

  generalNote: submission.note ?? '',

  rejectionReason: submission.rejectionReason,

  cancelledByName: submission.rejectedBy
    ? `${submission.rejectedBy.firstName} ${submission.rejectedBy.lastName}`
    : undefined,
});
